import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../config/axios';
import API_CONFIG from '../config/api';

const CreateCodePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [codeData, setCodeData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    checkUserAccess();
  }, []);

  const checkUserAccess = async () => {
    try {
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.AUTH.ME);
      
      if (response.status === 200) {
        const user = response.data;
        setUserData(user);
        
        // Check if user is logged in and part of an organization
        if (!user.organization_id) {
          setError('You must be part of an organization to create one-time codes.');
          setIsCheckingAccess(false);
          return;
        }
        
        setIsCheckingAccess(false);
      }
    } catch (error) {
      console.error('Access check error:', error);
      if (error.response?.status === 401) {
        setError('You must be logged in to access this page.');
      } else {
        setError('Failed to verify access. Please try again.');
      }
      setIsCheckingAccess(false);
    }
  };

  const handleCreateCode = async () => {
    setIsLoading(true);
    setError('');
    setSuccess(false);
    setCodeData(null);
    setIsCopied(false);

    try {
      const response = await apiClient.post('/auth/onetime');
      
      if (response.status === 200) {
        setCodeData(response.data);
        setSuccess(true);
      }
    } catch (error) {
      console.error('Create code error:', error);
      if (error.response) {
        setError(error.response.data?.message || 'Failed to create one-time code. Please try again.');
      } else if (error.request) {
        setError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyCode = async () => {
    if (codeData?.code) {
      try {
        await navigator.clipboard.writeText('https://3x.assessment.mekadealflow.co/page/1?code=' + codeData.code);
        setIsCopied(true);
        // Reset the copied state after 2 seconds
        setTimeout(() => setIsCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy code:', err);
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = codeData.code;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      }
    }
  };

  const formatExpirationDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (isCheckingAccess) {
    return (
      <div className="questionnaire-page">
        <div className="page-header">
          <div className="page-info">
            <h2>Create One-Time Code</h2>
            <p className="page-description">
              Verifying access permissions...
            </p>
          </div>
        </div>
        <div className="questions-container">
          <div className="question-block">
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="success-icon">⏳</div>
              <p>Checking permissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="questionnaire-page">
        <div className="page-header">
          <div className="page-info">
            <h2>Create One-Time Code</h2>
            <p className="page-description">
              Access denied
            </p>
          </div>
        </div>
        <div className="questions-container">
          <div className="question-block">
            <div className="error-message">
              {error}
            </div>
            <div className="auth-links">
              <button 
                className="link-button"
                onClick={() => navigate('/login')}
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <div className="page-info">
          <h2>Create One-Time Link</h2>
          <p className="page-description">
            Generate a one-time access link for your organization
          </p>
        </div>
      </div>

      <div className="questions-container">
        <div className="question-block no-hover">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && codeData && (
            <>
              <h3>One-time link created successfully!</h3>
              
              <div className="warning-message">
                <strong>Important:</strong> This is the only time you can view this link. Make sure to copy it now!
              </div>
              
              <div className="form-group">
                <label htmlFor="generated-link">Generated Link</label>
                <div className="code-input-container">
                  <input
                    id="generated-link"
                    type="text"
                    value={'https://3x.assessment.mekadealflow.co/page/1?code=' + codeData.code}
                    readOnly
                    className="code-input"
                  />
                  <button
                    type="button"
                    className={`copy-button ${isCopied ? 'copied' : ''}`}
                    onClick={handleCopyCode}
                  >
                    {isCopied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </div>

              <div className="expiration-simple">
                <p>Expires: {formatExpirationDate(codeData.expiration_date)}</p>
              </div>
            </>
          )}

          {!success && (
            <>
              <p>Click the button below to generate a new one-time link for your organization.</p>
              
              <button
                type="button"
                className="submit-btn"
                onClick={handleCreateCode}
                disabled={isLoading}
              >
                {isLoading ? 'Creating Link...' : 'Create One-Time Link'}
              </button>
            </>
          )}

          <div className="auth-links">
            <button 
              className="link-button"
              onClick={() => navigate('/me')}
            >
              Back to Profile
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateCodePage;
